<template>
  <div class="attachment-view">
    <div
      v-if="isLoaded && showPreview"
      key="preview-image"
      :href="currentUrl"
      target="_blank"
      rel="noopener nofollow ugc"
      class="attachment-preview"
    >
      <surface-attachment-preview-image
        draggable="false"
        :alt="title"
        class="attachment-preview-image"
        :src="previewImageSrc"
        :width="previewImageWidth"
        :height="previewImageHeight"
        :background-color="previewImageColor"
      />
      <div v-if="showMeta" class="attachment-preview-meta">
        <div class="attachment-preview-title ellipsified-type" :title="title">{{ title }}</div>
        <div v-if="description" class="attachment-preview-description ellipsified-type" :title="description">
          {{ description }}
        </div>
        <div v-if="showProvider && providerName" class="attachment-preview-provider">
          <div class="attachment-preview-provider-name ellipsified-type">{{ providerName }}</div>
        </div>
      </div>
    </div>
    <surface-attachment-preview-embed
      v-else-if="showEmbed"
      :actionable="actionable"
      :current-url="currentUrl"
      :is-downloadable="isDownloadable"
      :title="title"
      :preview-image-color="previewImageColor"
      :preview-image-aspect-ratio="previewImageAspectRatio"
      :preview-image-luminance="previewImageLuminance"
      :embed-html="embedHtml"
      :embed-meta-height="embedMetaHeight"
      @back="backToPreview"
      @zoom="zoom"
    />
    <div v-else key="spinner" class="attachment-loading">
      <OzLoadingSpinner />
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import { getDisplayAttributes } from 'bits/beethoven'

import SurfaceAttachmentPreviewImage from 'vuecomponents/surface_attachment_preview_image'
import SurfaceAttachmentPreviewEmbed from 'vuecomponents/surface_attachment_preview_embed'
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import { hyphenatedCurrentLocale as locale } from 'bits/current_locale'
import { $ } from 'bits/jquery'

const dpr = window.devicePixelRatio

export default {
  components: { SurfaceAttachmentPreviewImage, SurfaceAttachmentPreviewEmbed, OzLoadingSpinner },
  props: {
    url: String,
    width: Number,
    height: Number,
    actionable: {
      type: Boolean,
      default: false,
    },
    autocolor: {
      type: Boolean,
      default: true,
    },
    showProvider: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentUrl: null,
      showPreview: true,
      showEmbed: false,
      isLoaded: false,
      isTiny: false,
      isFallback: false,
      link: null,
      previewImageSrc: null,
      previewImageWidth: null,
      previewImageHeight: null,
      previewImageColor: null,
      previewImageAspectRatio: null,
      previewImageLuminance: null,
      showMeta: null,
      title: null,
      description: null,
      providerName: null,
      contentCategory: null,
      contentSubcategory: null,
      extension: null,
      embedCode: null,
      embedHtml: null,
      isDownloadable: null,
      embedMetaHeight: null,
    }
  },
  computed: {
    ...mapState(['isApp', 'isPhone', 'canPostMessage']),
    ...mapGetters(['useArvo']),
    metaStyle() {
      if (this.autocolor) {
        return { 'background-color': this.previewImageColor }
      }
      // TODO do this for now because of global styles that we don't want to change.
      return { 'background-color': 'transparent' }
    },
  },
  watch: {
    isLoaded(newValue) {
      if (newValue) {
        this.$emit('loaded')
      }
    },
  },
  mounted() {
    this.setState()
  },

  beforeUpdate() {
    this.setState()
  },
  methods: {
    ...mapActions('nativeApp', ['openLink']),
    unload() {
      this.currentUrl = null
      this.isLoaded = false
    },
    backToPreview() {
      this.showPreview = true
      this.showEmbed = false
      this.$emit('back-to-preview')
      // $(this.$el).parents(".wish").removeClass("is-playing is-zoomed");
    },
    async setState() {
      if (this.currentUrl === this.url) return
      if (this.currentUrl !== this.url) {
        this.backToPreview()
      }

      this.currentUrl = this.url

      if (!this.url) return

      const bhOptions = {
        dpr: dpr,
        locale: locale,
        width: this.width,
        height: this.height,
        useArvo: this.useArvo,
      }

      const data = await getDisplayAttributes(this.url, bhOptions)
      this.link = data
      this.isLoaded = true
      this.isTiny = data.is_tiny
      this.isFallback = data.is_fallback
      this.previewImageSrc = data.image_url
      this.previewImageWidth = data.image_width
      this.previewImageHeight = data.image_height
      this.previewImageColor = data.image_color
      this.previewImageAspectRatio = data.image_aspect_ratio
      this.previewImageLuminance = data.image_luminance
      this.showMeta = data.show_meta
      this.title = data.title
      this.description = data.description
      this.providerName = data.provider_name
      this.contentCategory = data.content_category
      this.contentSubcategory = data.content_subcategory
      this.extension = data.extension
      this.embedCode = data.embed_code
      this.isDownloadable = data.is_downloadable && this.isMobile
      if (this.contentCategory === 'file') {
        this.downloadFileName = this.title
      } else {
        this.downloadFileName = false
      }
    },
    play() {
      if (!this.actionable) {
        return false
      }

      this.playInApp()

      if ($(this.$el).parents('.noclick,.is-dragging').length > 0) {
        return false
      }
      if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
        this.embedMetaHeight = $(this.$refs.previewMeta).height() // do before unmount preview and mount embed
        this.embedInline()
        return false
      }
      if (this.isPhone) {
        if (this.contentCategory === 'photo') {
          this.zoom()
          return false
        }
      } else if (this.embedCode && this.contentSubcategory !== 'padlet') {
        this.zoom()
        return false
      }

      return true
    },
    playInApp() {
      let linkType = 'external'
      if (this.contentSubcategory === 'padlet') {
        linkType = 'padlet'
      } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
        linkType = 'internal_mediaplayer'
      } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
        linkType = 'internal_webview'
      }
      this.openLink({
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
        url: this.url,
        linkType,
      })
    },
    addQueryParams(url, paramsString) {
      if (url.match(/\?/)) {
        return [url, paramsString].join('&')
      }
      return [url, paramsString].join('?')
    },
    embedInline() {
      this.showPreview = false
      this.showEmbed = true
      this.embedHtml = this.embedCode

      const $embed = $(this.embedCode)

      if ($embed.is('iframe')) {
        $embed
          .attr({
            width: this.previewImageWidth,
            height: this.previewImageHeight,
          })
          .css('background', this.previewImageColor)
        const currentSrc = $embed.attr('src')
        let newSrc = this.addQueryParams(currentSrc, 'autoplay=1')
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = this.addQueryParams(newSrc, 'rel=0')
        }
        $embed.attr('src', newSrc)
        this.embedHtml = $embed[0].outerHTML
      }
      this.$emit('play')
      // $(this.$el).parents(".wish").addClass("is-playing");
    },
    zoom() {
      // TODO: implement for post
      // The code below should work, but it is not tested;
      // since the component is now only used for attachment preview before actual posting.
      // this.backToPreview();
      // this.dispatchStateChange({
      //   type: "ZOOM_POST",
      //   zoomedPostId: this.$parent.post.id,
      //   zoomedLink: this.link
      // });
      // if (this.$parent && this.$parent.post && this.$parent.post.id)
      //   $(this.root).parents(".wish").addClass("is-zoomed");
      //   this.$emit('zoom')
      // }
    },
    expandPost() {
      this.backToPreview()

      // if (!this.actionable) {
      //   this.dispatchStateChange({
      //     type: "EXPAND_POST",
      //     post: this.$parent.post
      //   });
      // }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.attachment-view {
  position: relative;
  display: flex;
  flex-direction: column;
}
.attachment-preview-meta {
  padding: 8px 12px;
  font-size: 12px;
  line-height: 16px;
}
.attachment-preview-title {
  font-weight: 500;
}
.attachment-preview-provider-name {
  font-size: 11px;
  color: var(--caption-text-color);
  text-transform: lowercase;
}
.attachment-loading {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
